import React, { useCallback } from 'react';
import { classes, type GlobalProps, useSlug } from '@bloomberg/media-ui-core';

import { Watchlist, WatchlistSaveForm } from '../types';

import { AddToWatchlist } from './AddToWatchlist';
import { AddToWatchlistLoggedOut } from './AddToWatchlistLoggedOut';
import { Error } from './Error';
import { QuickAddSubscribe } from './QuickAddSubscribe';

import styles from './QuickAddOverlay.module.css';

export type QuickAddOverlayProps = GlobalProps & {
  canAddWatchlist: boolean;
  isFollowing?: boolean;
  isLoggedIn?: boolean;
  onClose: () => void;
  onCreateNewWatchlist: (watchlistName: string) => Promise<number | null>;
  onCreateAccountClick: () => void;
  onSignInClick: () => void;
  onSave: (saveForm: WatchlistSaveForm) => void;
  showError?: boolean;
  watchlists: Watchlist[];
  trackClick?: (eventAction: string, eventLabel: string) => void;
  disabled?: boolean;
  isSubscribed: boolean;
  name?: string;
};

export const QuickAddOverlay = React.forwardRef<
  HTMLDivElement,
  QuickAddOverlayProps
>(function QuickAddOverlayComponent(props: QuickAddOverlayProps, ref) {
  const {
    canAddWatchlist,
    isLoggedIn = false,
    name = 'quick-add-overlay',
    onClose = null,
    onCreateAccountClick,
    onCreateNewWatchlist,
    onSave,
    onSignInClick,
    showError = false,
    watchlists = [],
    trackClick,
    disabled = true,
    isSubscribed,
  } = props;
  const componentName = useSlug(name);

  const cx = classes([styles.quickAddOverlay], {
    ...props,
    name,
  });

  const handleOnCloseClick = useCallback(() => {
    onClose?.();
  }, [onClose]);

  function renderWatchlistContent({
    error,
    isDisabled,
    loggedIn,
    subscribed,
  }: {
    error: boolean;
    isDisabled: boolean;
    loggedIn: boolean;
    subscribed: boolean;
  }) {
    if (error) {
      return <Error />;
    }

    if (isDisabled) {
      return loggedIn ? (
        <AddToWatchlist
          trackClick={trackClick}
          canAddWatchlist={canAddWatchlist}
          onClose={handleOnCloseClick}
          onCreateNewWatchlist={onCreateNewWatchlist}
          onSave={onSave}
          watchlists={watchlists}
        />
      ) : (
        <AddToWatchlistLoggedOut
          onCreateAccountClick={onCreateAccountClick}
          onClose={handleOnCloseClick}
          onSignInClick={onSignInClick}
        />
      );
    }

    if (loggedIn) {
      return subscribed ? (
        <AddToWatchlist
          trackClick={trackClick}
          canAddWatchlist={canAddWatchlist}
          onClose={handleOnCloseClick}
          onCreateNewWatchlist={onCreateNewWatchlist}
          onSave={onSave}
          watchlists={watchlists}
        />
      ) : (
        <QuickAddSubscribe
          onClose={handleOnCloseClick}
          onSignInClick={onSignInClick}
          isLoggedIn={loggedIn}
        />
      );
    }

    return (
      <QuickAddSubscribe
        onClose={handleOnCloseClick}
        onSignInClick={onSignInClick}
        isLoggedIn={loggedIn}
      />
    );
  }

  return (
    <div className={cx} ref={ref} data-component={componentName}>
      {renderWatchlistContent({
        error: showError,
        isDisabled: disabled,
        loggedIn: isLoggedIn,
        subscribed: isSubscribed,
      })}
    </div>
  );
});
